<template>
  <div class="w-full relative sm:flex">
    <div
      class="flex-initial flex-grow"
      :class="{'opacity-50 pointer-events-none': submittingOrder > 0}">
      <slot
        :order="order"
        :loading-order="$apollo.queries.order.loading > 0"
        :saving-product-by-id="savingProductById"
        :update-order="updateOrder"
        :changed-products="changedProducts"
      />
    </div>
    <Cart
      class="max-w-sm fex-none pl-6"
      :orderable-by-system="systemStatus && systemStatus.orderable"
      :order="order"
      :loading="$apollo.queries.order.loading > 0"
      :submitting="submittingOrder > 0"
      :errors="validationErrors"
      :transaction-errors="transactionErrorMessage"
      :change-log="changedProducts"
      @submit="onSubmitOrder"
      @delete="deleteOrder"
    />
  </div>
</template>

<script>
import { orderQuery } from './queries'
import { upsertOrder, submitOrder, submitChangelog, deleteOrder } from './mutations'

import Cart from '@/Components/Product/Cart'

export default {
  name: 'CartProvider',
  components: { Cart },
  inject: ['systemStatus'],

  props: {
    orderDate: {
      type: String,
      required: true,
    },
    orderId: {
      type: Number,
      default: null
    },
  },

  data() {
    return {
      order: null,
      loading: 0,
      submittingOrder: 0,
      savingProductById: 0,
      validationErrors: [],
      transactionErrorMessage: null,

      /**
       * Changed products are product objects with quantities of already submitted orders,
       * which are not jet upserted, only after a direct user opt-in.
       */
      changedProducts: [],
    }
  },

  apollo: {
    order: {
      query: orderQuery,
      variables () {
        return {
          date: this.orderDate
        }
      },
    }
  },

  methods: {
    async updateOrder(data) {
      if (this.order && this.order.submitted) {
        this.updateProductChangelog(data)
      } else {
        await this.upsertOrder(data)
      }
    },

    updateProductChangelog({ item, quantity }) {
      const index = this.changedProducts.findIndex((obj => obj.id === item.id))

      if (index > -1) {
        // Remove from array
        if (quantity === 0 && this.changedProducts[index].type === 'new') {
          this.changedProducts.splice(index, 1)
        } else {
          let product = this.order.products.find(p => p.id === item.id)
          // remove from changelog if quantity is same as before
          if (product && product.pivot.quantity === quantity) {
            this.changedProducts.splice(index, 1)
          } else {
            this.changedProducts[index].quantity = quantity
          }
        }
      } else {
        this.changedProducts.push({
          id:item.id,
          type: this.order.products.find(p => p.id === item.id ) ? 'change' : 'new',
          item,
          quantity
        })
      }
    },

    async upsertOrder({ item: { id }, quantity }) {
      const stock = []
      this.validationErrors = []
      this.transactionErrorMessage = null
      this.savingProductById = id

      if (this.order && this.order.products.length > 0) {
        this.order.products.map(product => {
          if (product.id === id && quantity !== 0) {
            stock.push({ id, quantity })
          }
          else if (product.id !== id) {
            stock.push({
              id: product.id,
              quantity: product.pivot.quantity
            })
          }
        })

        if (!this.order.products.find(product => product.id === id)) {
          stock.push({ id, quantity })
        }
      }
      // new order without any entries
      else {
        stock.push({ id, quantity })
      }

      try {
        await this.$apollo.mutate({
          mutation: upsertOrder,
          variables: {
            id: this.orderId,
            date: this.orderDate,
            products: {
              sync: stock
            }
          }
        })
        this.$apollo.queries.order.refetch({ variables: { date: this.orderDate } })
      } catch (error) {
        this.report(error)
      }

      this.savingProductById = null
    },

    async onSubmitOrder(order) {
      if (this.order && this.order.submitted) {
        await this.submitChangeLog(order)
      } else {
        await this.submitOrder(order)
      }
    },

    async submitOrder(order) {
      this.submittingOrder++
      this.validationErrors = []

      try {
        await this.$apollo.mutate({
          mutation: submitOrder,
          variables: { id: order.id }
        })

        window.location.reload()
      } catch (error) {
        this.report(error)
      }

      this.submittingOrder--
      this.validationErrors = []
    },

    /**
     * Used to apply the changelog and resubmit the order.
     */
    async submitChangeLog(order) {
      const stock = []

      this.submittingOrder++
      this.validationErrors = []


      if (!this.order || this.order.products.length === 0 || this.changedProducts.length === 0) {
        return false
      }

      // Add new products
      this.changedProducts.map(({ id, quantity, type }) =>
        type === 'new' && stock.push({ id, quantity }
      ))

      let remainingProducts = this.order.products.filter(product => {
        let changedProduct = this.changedProducts.find(p => p.id === product.id)
        if ( changedProduct && changedProduct.quantity !== 0) {
          stock.push({ id: product.id, quantity: changedProduct.quantity })
          return false
        }

       return !changedProduct
      })

      remainingProducts.map(({id, pivot: { quantity }}) => stock.push({ id, quantity }))

      try {
        await this.$apollo.mutate({
          mutation: submitChangelog,
          variables: {
            id: order.id,
            date: this.orderDate,
            products: {
              sync: stock
            }
          }
        })

        window.location.reload()
      } catch (error) {
        this.report(error)
      }

      this.submittingOrder--
    },

    async deleteOrder(order) {
      this.submittingOrder++
      this.validationErrors = []

      try {
        await this.$apollo.mutate({
          mutation: deleteOrder,
          variables: {
            id: order.id,
            submitted: order.submitted
          }
        })

        window.location.replace(window.location.href + "?deleted=true")
      } catch (error) {
        this.report(error)
      }

      this.submittingOrder--
      this.validationErrors = []
    },

    report(error) {
      if (error.graphQLErrors) {
        error.graphQLErrors.map(err => {
          if (err.extensions.category === 'validation') {
            for (let key in err.extensions.validation) {
              this.validationErrors.push(err.extensions.validation[key][0])
            }
          } else {
            this.transactionErrorMessage = error.message
          }
        })
      } else {
        console.error(error)
      }
    }
  }
}
</script>
